import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'
import * as React from 'react'
import { useMediaQuery } from 'usehooks-ts'

import { cn } from '@/lib/utils'

const ResponsiveModal = DialogPrimitive.Root

const ResponsiveModalTrigger = DialogPrimitive.Trigger

const ResponsiveModalClose = DialogPrimitive.Close

const ResponsiveModalPortal = DialogPrimitive.Portal

const ResponsiveModalOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		className={cn(
			'fixed inset-0 z-50 bg-black/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className,
		)}
		{...props}
		ref={ref}
	/>
))
ResponsiveModalOverlay.displayName = DialogPrimitive.Overlay.displayName

const ResponsiveModalVariants = cva(
	cn(
		'fixed z-50 gap-4 bg-background max-lg:px-5 max-lg:py-6 lg:pt-10 lg:pb-6 lg:px-8 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 overflow-y-auto',
		'lg:left-[50%] lg:top-[50%] lg:grid lg:w-full lg:max-w-4xl lg:translate-x-[-50%] lg:translate-y-[-50%] lg:border lg:duration-200 lg:data-[state=open]:animate-in lg:data-[state=closed]:animate-out lg:data-[state=closed]:fade-out-0 lg:data-[state=open]:fade-in-0 lg:data-[state=closed]:zoom-out-95 lg:data-[state=open]:zoom-in-95 lg:data-[state=closed]:slide-out-to-left-1/2 lg:data-[state=closed]:slide-out-to-top-[48%] lg:data-[state=open]:slide-in-from-left-1/2 lg:data-[state=open]:slide-in-from-top-[48%] lg:rounded-xl',
	),
	{
		variants: {
			side: {
				top: 'inset-x-0 top-0 border-b rounded-b-xl max-h-[90%] lg:h-fit data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
				bottom:
					'inset-x-0 bottom-0 border-t lg:h-fit max-h-[90%] rounded-t-xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
				left: 'inset-y-0 left-0 h-full lg:h-fit w-3/4 border-r rounded-r-xl data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
				right:
					'inset-y-0 right-0 h-full lg:h-fit w-3/4 border-l rounded-l-xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
			},
		},
		defaultVariants: {
			side: 'bottom',
		},
	},
)

interface ResponsiveModalContentProps
	extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
		VariantProps<typeof ResponsiveModalVariants> {
	crossIconClassName?: string
}

interface DraggableResponsiveModalContentProps extends ResponsiveModalContentProps {
	defaultPosition?: { x: number; y: number }
}

const ResponsiveModalContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	ResponsiveModalContentProps
>(({ side = 'bottom', className, crossIconClassName, children, ...props }, ref) => (
	<ResponsiveModalPortal>
		<ResponsiveModalOverlay />
		<DialogPrimitive.Content
			ref={ref}
			className={cn(ResponsiveModalVariants({ side }), className)}
			{...props}>
			{children}
			<ResponsiveModalClose
				className={cn(
					'absolute cursor-pointer right-5 lg:right-8 lg:top-10 top-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary',
					crossIconClassName,
				)}>
				<X className="h-6 w-6" />
				<span className="sr-only">Close</span>
			</ResponsiveModalClose>
		</DialogPrimitive.Content>
	</ResponsiveModalPortal>
))
ResponsiveModalContent.displayName = DialogPrimitive.Content.displayName

const DraggableResponsiveModalContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	DraggableResponsiveModalContentProps
>(
	(
		{
			side = 'bottom',
			className,
			crossIconClassName,
			children,
			defaultPosition = { x: 0, y: 0 },
			style,
			...props
		},
		ref,
	) => {
		const isDesktop = useMediaQuery('(min-width: 64rem)')

		const [position, setPosition] = React.useState(defaultPosition)
		const modalRef = React.useRef<HTMLDivElement | null>(null)
		const draggingRef = React.useRef(false)
		const lastMouse = React.useRef({ x: 0, y: 0 })

		const SNAP_THRESHOLD = 40

		React.useEffect(() => {
			setPosition(defaultPosition)
		}, [defaultPosition.x, defaultPosition.y])

		const onMouseDown = (e: React.MouseEvent) => {
			const target = e.target as HTMLElement
			if (target.closest('button, input, textarea')) return

			draggingRef.current = true
			lastMouse.current = { x: e.clientX, y: e.clientY }

			document.addEventListener('mousemove', onMouseMove)
			document.addEventListener('mouseup', onMouseUp)
		}

		const onMouseMove = (e: MouseEvent) => {
			if (!draggingRef.current) return

			const deltaX = e.clientX - lastMouse.current.x
			const deltaY = e.clientY - lastMouse.current.y

			lastMouse.current = { x: e.clientX, y: e.clientY }

			setPosition(prev => {
				const nextX = prev.x + deltaX
				const nextY = prev.y + deltaY

				const rect = modalRef.current?.getBoundingClientRect()

				const modalWidth = rect?.width || 800
				const modalHeight = rect?.height || 600

				const maxX = window.innerWidth / 2 - modalWidth / 2
				const maxY = window.innerHeight / 2 - modalHeight / 2

				let clampedX = Math.max(-maxX, Math.min(maxX, nextX))
				let clampedY = Math.max(-maxY, Math.min(maxY, nextY))

				if (Math.abs(clampedX + maxX) < SNAP_THRESHOLD) {
					clampedX = -maxX
				} else if (Math.abs(clampedX - maxX) < SNAP_THRESHOLD) {
					clampedX = maxX
				}

				if (Math.abs(clampedY + maxY) < SNAP_THRESHOLD) {
					clampedY = -maxY
				} else if (Math.abs(clampedY - maxY) < SNAP_THRESHOLD) {
					clampedY = maxY
				}

				return { x: clampedX, y: clampedY }
			})
		}
		const onMouseUp = () => {
			draggingRef.current = false
			document.removeEventListener('mousemove', onMouseMove)
			document.removeEventListener('mouseup', onMouseUp)
		}

		if (!isDesktop) {
			return (
				<ResponsiveModalContent
					ref={ref}
					side={side}
					className={className}
					crossIconClassName={crossIconClassName}
					{...props}>
					{children}
				</ResponsiveModalContent>
			)
		}

		return (
			<ResponsiveModalPortal>
				<ResponsiveModalOverlay />

				<DialogPrimitive.Content
					ref={modalRef}
					onMouseDown={onMouseDown}
					className={cn(
						'fixed left-1/2 top-1/2 transition-transform duration-150 ease-out z-50 w-full max-w-4xl bg-background shadow-lg rounded-xl border p-6',
						'cursor-grab active:cursor-grabbing',
						className,
					)}
					style={{
						...style,
						transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px)`,
					}}
					{...props}>
					{children}

					<ResponsiveModalClose
						className={cn(
							'absolute right-6 top-6 opacity-70 hover:opacity-100',
							crossIconClassName,
						)}>
						<X className="h-6 w-6" />
						<span className="sr-only">Close</span>
					</ResponsiveModalClose>
				</DialogPrimitive.Content>
			</ResponsiveModalPortal>
		)
	},
)

DraggableResponsiveModalContent.displayName = 'DraggableResponsiveModalContent'

const ResponsiveModalHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
)
ResponsiveModalHeader.displayName = 'ResponsiveModalHeader'

const ResponsiveModalFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
		{...props}
	/>
)
ResponsiveModalFooter.displayName = 'ResponsiveModalFooter'

const ResponsiveModalTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn('text-sm lg:text-xl text-secondary font-extrabold', className)}
		{...props}
	/>
))
ResponsiveModalTitle.displayName = DialogPrimitive.Title.displayName

const ResponsiveModalDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn('text-sm text-muted-foreground', className)}
		{...props}
	/>
))
ResponsiveModalDescription.displayName = DialogPrimitive.Description.displayName

export {
	DraggableResponsiveModalContent,
	ResponsiveModal,
	ResponsiveModalClose,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalFooter,
	ResponsiveModalHeader,
	ResponsiveModalOverlay,
	ResponsiveModalPortal,
	ResponsiveModalTitle,
	ResponsiveModalTrigger,
}
